import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { shipmentsAPI, warehousesAPI, locationsAPI, suppliersAPI } from '@/lib/api';
import { getToday, formatDate } from '@/lib/utils';
import { 
  FileText, 
  Download,
  Calendar,
  Filter,
  Store,
  Package,
  TrendingUp,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  Building2,
  Truck
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  Legend,
  PieChart,
  Pie,
  Cell
} from 'recharts';

const AdminReportsPage = () => {
  const [shipments, setShipments] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [locations, setLocations] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filtros - usar zona horaria de Chile
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toLocaleDateString('en-CA', { timeZone: 'America/Santiago' });
  });
  const [endDate, setEndDate] = useState(() => getToday());
  const [selectedWarehouse, setSelectedWarehouse] = useState('all');
  const [selectedLocation, setSelectedLocation] = useState('all');
  const [selectedSupplier, setSelectedSupplier] = useState('all');
  
  // Estado de expansión para detalles por día
  const [expandedDays, setExpandedDays] = useState({});

  // Colores para gráficos
  const COLORS = ['#E07B54', '#F5C85D', '#4CAF50', '#2196F3', '#9C27B0', '#FF5722', '#607D8B', '#795548'];

  // Cargar datos
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [shipmentsRes, warehousesRes, locationsRes, suppliersRes] = await Promise.all([
        shipmentsAPI.getAll({
          warehouse_id: selectedWarehouse !== 'all' ? selectedWarehouse : undefined,
          location_id: selectedLocation !== 'all' ? selectedLocation : undefined,
          start_date: startDate,
          end_date: endDate,
          limit: 500
        }),
        warehousesAPI.getActive(),
        locationsAPI.getAll(true),
        suppliersAPI.getAll().catch(() => ({ data: [] }))
      ]);
      
      setShipments(shipmentsRes.data || []);
      setWarehouses(warehousesRes.data || []);
      setLocations(locationsRes.data || []);
      setSuppliers(suppliersRes.data || []);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, selectedWarehouse, selectedLocation]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Filtrar envíos por proveedor (a nivel de items)
  const filteredShipments = useMemo(() => {
    if (selectedSupplier === 'all') return shipments;
    
    return shipments.map(shipment => ({
      ...shipment,
      items: shipment.items.filter(item => 
        item.supplier?.toLowerCase().includes(selectedSupplier.toLowerCase())
      )
    })).filter(shipment => shipment.items.length > 0);
  }, [shipments, selectedSupplier]);

  // Extraer proveedores únicos de los envíos
  const uniqueSuppliers = useMemo(() => {
    const suppliersSet = new Set();
    shipments.forEach(shipment => {
      shipment.items?.forEach(item => {
        if (item.supplier) suppliersSet.add(item.supplier);
      });
    });
    return Array.from(suppliersSet).sort();
  }, [shipments]);

  // Agrupar por día
  const shipmentsByDay = useMemo(() => {
    const byDay = {};
    
    filteredShipments.forEach(shipment => {
      const day = shipment.created_at?.split('T')[0] || 'unknown';
      if (!byDay[day]) {
        byDay[day] = {
          date: day,
          shipments: [],
          totalShipments: 0,
          totalItems: 0,
          totalQuantity: 0,
          byLocation: {},
          bySupplier: {}
        };
      }
      
      byDay[day].shipments.push(shipment);
      byDay[day].totalShipments += 1;
      byDay[day].totalItems += shipment.items?.length || 0;
      byDay[day].totalQuantity += shipment.items?.reduce((sum, item) => sum + (item.quantity || 0), 0) || 0;
      
      // Por local
      const locName = shipment.location_name || 'Desconocido';
      if (!byDay[day].byLocation[locName]) {
        byDay[day].byLocation[locName] = { count: 0, quantity: 0 };
      }
      byDay[day].byLocation[locName].count += 1;
      byDay[day].byLocation[locName].quantity += shipment.items?.reduce((sum, item) => sum + (item.quantity || 0), 0) || 0;
      
      // Por proveedor
      shipment.items?.forEach(item => {
        const supplier = item.supplier || 'Sin proveedor';
        if (!byDay[day].bySupplier[supplier]) {
          byDay[day].bySupplier[supplier] = { count: 0, quantity: 0 };
        }
        byDay[day].bySupplier[supplier].count += 1;
        byDay[day].bySupplier[supplier].quantity += item.quantity || 0;
      });
    });
    
    // Convertir a array y ordenar por fecha descendente
    return Object.values(byDay).sort((a, b) => b.date.localeCompare(a.date));
  }, [filteredShipments]);

  // KPIs totales
  const totals = useMemo(() => {
    return {
      totalShipments: filteredShipments.length,
      totalItems: filteredShipments.reduce((sum, s) => sum + (s.items?.length || 0), 0),
      totalQuantity: filteredShipments.reduce((sum, s) => 
        sum + (s.items?.reduce((iSum, item) => iSum + (item.quantity || 0), 0) || 0), 0),
      uniqueLocations: new Set(filteredShipments.map(s => s.location_name)).size,
      uniqueSuppliers: uniqueSuppliers.length
    };
  }, [filteredShipments, uniqueSuppliers]);

  // Datos para gráfico de barras por local
  const chartDataByLocation = useMemo(() => {
    const byLocation = {};
    filteredShipments.forEach(shipment => {
      const loc = shipment.location_name || 'Desconocido';
      if (!byLocation[loc]) {
        byLocation[loc] = { name: loc, envios: 0, unidades: 0 };
      }
      byLocation[loc].envios += 1;
      byLocation[loc].unidades += shipment.items?.reduce((sum, item) => sum + (item.quantity || 0), 0) || 0;
    });
    return Object.values(byLocation).sort((a, b) => b.unidades - a.unidades);
  }, [filteredShipments]);

  // Datos para gráfico de pie por proveedor
  const chartDataBySupplier = useMemo(() => {
    const bySupplier = {};
    filteredShipments.forEach(shipment => {
      shipment.items?.forEach(item => {
        const supplier = item.supplier || 'Sin proveedor';
        if (!bySupplier[supplier]) {
          bySupplier[supplier] = { name: supplier, value: 0 };
        }
        bySupplier[supplier].value += item.quantity || 0;
      });
    });
    return Object.values(bySupplier).sort((a, b) => b.value - a.value).slice(0, 8);
  }, [filteredShipments]);

  // Toggle expandir día
  const toggleDay = (day) => {
    setExpandedDays(prev => ({ ...prev, [day]: !prev[day] }));
  };

  // Exportar a CSV
  const exportToCSV = () => {
    const headers = ['Fecha', 'Hora', 'Local', 'Bodega', 'Producto', 'Código', 'Proveedor', 'Cantidad', 'Unidad', 'Notas'];
    const rows = [];
    
    filteredShipments.forEach(shipment => {
      const fecha = shipment.created_at?.split('T')[0] || '';
      const hora = shipment.created_at?.split('T')[1]?.substring(0, 5) || '';
      
      shipment.items?.forEach(item => {
        rows.push([
          fecha,
          hora,
          shipment.location_name || '',
          shipment.warehouse_name || '',
          item.item_name || '',
          item.item_code || '',
          item.supplier || '',
          item.quantity || 0,
          item.unit || '',
          shipment.notes || ''
        ]);
      });
    });
    
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `reporte_envios_${startDate}_${endDate}.csv`;
    link.click();
    
    toast.success('Reporte exportado');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-reports-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold text-slate-900">Reportes de Envíos</h1>
          <p className="text-muted-foreground mt-1">Análisis detallado de envíos a locales por fecha, proveedor y producto</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={loadData} variant="outline" size="sm">
            <RefreshCw className="w-4 h-4 mr-2" />
            Actualizar
          </Button>
          <Button onClick={exportToCSV} variant="default" size="sm">
            <Download className="w-4 h-4 mr-2" />
            Exportar CSV
          </Button>
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-lg border border-slate-200 p-4 shadow-sm">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          <div>
            <Label className="text-sm font-medium mb-1.5 block">Fecha Inicio</Label>
            <Input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>
          
          <div>
            <Label className="text-sm font-medium mb-1.5 block">Fecha Fin</Label>
            <Input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
          
          <div>
            <Label className="text-sm font-medium mb-1.5 block">Bodega</Label>
            <Select value={selectedWarehouse} onValueChange={setSelectedWarehouse}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas las bodegas</SelectItem>
                {warehouses.map(wh => (
                  <SelectItem key={wh.id} value={wh.id}>{wh.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <Label className="text-sm font-medium mb-1.5 block">Local</Label>
            <Select value={selectedLocation} onValueChange={setSelectedLocation}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los locales</SelectItem>
                {locations.map(loc => (
                  <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div>
            <Label className="text-sm font-medium mb-1.5 block">Proveedor</Label>
            <Select value={selectedSupplier} onValueChange={setSelectedSupplier}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los proveedores</SelectItem>
                {uniqueSuppliers.map(supplier => (
                  <SelectItem key={supplier} value={supplier}>{supplier}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-lg border border-slate-200 p-4 shadow-sm"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <Truck className="w-5 h-5 text-primary" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{totals.totalShipments}</p>
              <p className="text-xs text-muted-foreground">Envíos</p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-lg border border-slate-200 p-4 shadow-sm"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-50 rounded-lg flex items-center justify-center">
              <Package className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-green-600">{totals.totalQuantity.toLocaleString()}</p>
              <p className="text-xs text-muted-foreground">Unidades</p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-lg border border-slate-200 p-4 shadow-sm"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
              <FileText className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-blue-600">{totals.totalItems}</p>
              <p className="text-xs text-muted-foreground">Productos</p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-lg border border-slate-200 p-4 shadow-sm"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-50 rounded-lg flex items-center justify-center">
              <Store className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-amber-600">{totals.uniqueLocations}</p>
              <p className="text-xs text-muted-foreground">Locales</p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white rounded-lg border border-slate-200 p-4 shadow-sm"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-50 rounded-lg flex items-center justify-center">
              <Building2 className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-purple-600">{totals.uniqueSuppliers}</p>
              <p className="text-xs text-muted-foreground">Proveedores</p>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Gráficos */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Gráfico por Local */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-lg border border-slate-200 p-6 shadow-sm"
        >
          <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <Store className="w-5 h-5 text-primary" />
            Unidades por Local
          </h3>
          {chartDataByLocation.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={chartDataByLocation} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis type="number" tick={{ fontSize: 12 }} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={100} />
                <Tooltip 
                  contentStyle={{ backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px' }}
                  formatter={(value, name) => [value.toLocaleString(), name === 'unidades' ? 'Unidades' : 'Envíos']}
                />
                <Bar dataKey="unidades" name="Unidades" fill="#E07B54" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[300px] flex items-center justify-center text-muted-foreground">
              No hay datos para mostrar
            </div>
          )}
        </motion.div>

        {/* Gráfico por Proveedor */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-lg border border-slate-200 p-6 shadow-sm"
        >
          <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <Building2 className="w-5 h-5 text-purple-600" />
            Distribución por Proveedor
          </h3>
          {chartDataBySupplier.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={chartDataBySupplier}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name.substring(0, 10)}${name.length > 10 ? '...' : ''} (${(percent * 100).toFixed(0)}%)`}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {chartDataBySupplier.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip 
                  contentStyle={{ backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px' }}
                  formatter={(value) => [value.toLocaleString() + ' unidades']}
                />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[300px] flex items-center justify-center text-muted-foreground">
              No hay datos para mostrar
            </div>
          )}
        </motion.div>
      </div>

      {/* Detalle por Día */}
      <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200">
          <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
            <Calendar className="w-5 h-5 text-slate-500" />
            Detalle de Envíos por Día
          </h3>
        </div>
        
        {shipmentsByDay.length === 0 ? (
          <div className="p-12 text-center">
            <Truck className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-900">No hay envíos en este período</h3>
            <p className="text-muted-foreground mt-1">Ajusta los filtros para ver datos</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {shipmentsByDay.map((dayData) => (
              <div key={dayData.date}>
                {/* Header del día */}
                <div 
                  onClick={() => toggleDay(dayData.date)}
                  className="px-6 py-4 bg-slate-50 hover:bg-slate-100 cursor-pointer flex items-center justify-between"
                >
                  <div className="flex items-center gap-4">
                    {expandedDays[dayData.date] ? (
                      <ChevronDown className="w-5 h-5 text-slate-500" />
                    ) : (
                      <ChevronRight className="w-5 h-5 text-slate-500" />
                    )}
                    <div>
                      <p className="font-semibold text-slate-900">
                        {formatDate(dayData.date + 'T12:00:00')}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {dayData.totalShipments} envío(s) • {dayData.totalItems} productos • {dayData.totalQuantity.toLocaleString()} unidades
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-4 text-sm">
                    <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded">
                      {Object.keys(dayData.byLocation).length} locales
                    </span>
                    <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded">
                      {Object.keys(dayData.bySupplier).length} proveedores
                    </span>
                  </div>
                </div>
                
                {/* Detalle expandido */}
                {expandedDays[dayData.date] && (
                  <div className="px-6 py-4 bg-white">
                    {/* Resumen por local */}
                    <div className="mb-4">
                      <h4 className="text-sm font-semibold text-slate-700 mb-2">Por Local:</h4>
                      <div className="flex flex-wrap gap-2">
                        {Object.entries(dayData.byLocation).map(([loc, data]) => (
                          <span key={loc} className="text-xs bg-slate-100 px-2 py-1 rounded">
                            {loc}: <strong>{data.quantity}</strong> unidades ({data.count} envíos)
                          </span>
                        ))}
                      </div>
                    </div>
                    
                    {/* Resumen por proveedor */}
                    <div className="mb-4">
                      <h4 className="text-sm font-semibold text-slate-700 mb-2">Por Proveedor:</h4>
                      <div className="flex flex-wrap gap-2">
                        {Object.entries(dayData.bySupplier).map(([supplier, data]) => (
                          <span key={supplier} className="text-xs bg-purple-50 text-purple-700 px-2 py-1 rounded">
                            {supplier}: <strong>{data.quantity}</strong> unidades
                          </span>
                        ))}
                      </div>
                    </div>
                    
                    {/* Tabla de envíos del día */}
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-slate-50 text-slate-600 text-xs uppercase">
                          <tr>
                            <th className="px-3 py-2 text-left">Hora</th>
                            <th className="px-3 py-2 text-left">Local</th>
                            <th className="px-3 py-2 text-left">Producto</th>
                            <th className="px-3 py-2 text-left">Proveedor</th>
                            <th className="px-3 py-2 text-right">Cantidad</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {dayData.shipments.flatMap(shipment => 
                            shipment.items?.map((item, idx) => (
                              <tr key={`${shipment.id}-${idx}`} className="hover:bg-slate-50">
                                <td className="px-3 py-2 text-slate-500">
                                  {shipment.created_at?.split('T')[1]?.substring(0, 5) || '-'}
                                </td>
                                <td className="px-3 py-2 font-medium">{shipment.location_name}</td>
                                <td className="px-3 py-2">
                                  <span className="font-medium">{item.item_name}</span>
                                  <span className="text-xs text-slate-400 ml-1">({item.item_code})</span>
                                </td>
                                <td className="px-3 py-2 text-slate-600">{item.supplier || '-'}</td>
                                <td className="px-3 py-2 text-right font-semibold">
                                  {item.quantity} {item.unit}
                                </td>
                              </tr>
                            )) || []
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminReportsPage;
